import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Store, MapPin, Crown, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const ROTATE_MS = 5500;

/**
 * Rotating sponsor spotlight. Shows one sponsor at a time with logo/initial,
 * description, package tier, and a CTA that deep-links to their profile.
 * Auto-rotates every 5.5s, pauses on hover, has prev/next + dot indicators.
 */
export default function SponsorSpotlight() {
  const [sponsors, setSponsors] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sponsors");
        setSponsors(data.sponsors || []);
      } catch {
        setSponsors([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (paused || sponsors.length < 2) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % sponsors.length);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, sponsors.length]);

  if (sponsors.length === 0) return null;

  const s = sponsors[idx % sponsors.length];
  const tierGold = s.package === "city_takeover";
  const tierGreen = s.package === "featured";
  const accent = tierGold ? "#D7A62A" : tierGreen ? "#76B82A" : "#F5F2E8";
  const initial = (s.business_name || "?")[0].toUpperCase();

  return (
    <section
      className="loot-card-flat -mx-4 md:mx-0 p-6 md:p-8 relative overflow-hidden"
      style={{
        borderColor: accent,
        background: tierGold
          ? "linear-gradient(135deg, rgba(215,166,42,0.10), rgba(15,15,15,1) 55%)"
          : tierGreen
          ? "linear-gradient(135deg, rgba(118,184,42,0.08), rgba(15,15,15,1) 55%)"
          : "linear-gradient(135deg, rgba(245,242,232,0.04), rgba(15,15,15,1) 55%)",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="sponsor-spotlight"
    >
      {tierGold && (
        <Crown
          size={200}
          aria-hidden="true"
          className="absolute -right-8 -top-8 opacity-10"
          style={{ color: "#D7A62A" }}
        />
      )}
      <div className="flex items-center justify-between mb-4 relative">
        <div className="flex items-center gap-2">
          <Store size={14} style={{ color: accent }} />
          <span className="overline" style={{ color: accent }}>
            Sponsor spotlight
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIdx((i) => (i - 1 + sponsors.length) % sponsors.length)}
            className="p-1.5 text-[#F5F2E8]/60 hover:text-[#F5F2E8]"
            aria-label="Previous sponsor"
            data-testid="spotlight-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % sponsors.length)}
            className="p-1.5 text-[#F5F2E8]/60 hover:text-[#F5F2E8]"
            aria-label="Next sponsor"
            data-testid="spotlight-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <Link
        to={`/sponsors/${s.id}`}
        className="flex flex-col md:flex-row gap-6 md:items-center relative group"
        data-testid={`spotlight-link-${s.id}`}
      >
        <div
          className="w-20 h-20 md:w-28 md:h-28 border flex items-center justify-center shrink-0 font-display text-5xl md:text-6xl"
          style={{
            borderColor: accent,
            background: "#123A1A",
            color: accent,
          }}
          aria-hidden="true"
        >
          {s.logo_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${s.logo_url}`}
              alt={s.business_name}
              className="w-full h-full object-cover"
            />
          ) : (
            initial
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tag-pill uppercase" style={{ color: accent, borderColor: accent }}>
              {s.package.replace("_", " ")}
            </span>
            {s.city && (
              <span className="font-mono text-[10px] text-[#F5F2E8]/50 flex items-center gap-1 uppercase tracking-widest">
                <MapPin size={10} /> {s.city}
              </span>
            )}
          </div>
          <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight mt-2 leading-[0.95]">
            {s.business_name}
          </h3>
          <p className="mt-2 text-sm md:text-base text-[#F5F2E8]/75 line-clamp-2 max-w-lg">
            {s.description || "Proud local supporter of The Local Loot."}
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs font-mono">
            <span className="text-[#F5F2E8]/60">
              {s.drops_count} drop{s.drops_count === 1 ? "" : "s"} funded
            </span>
            <span className="text-[#F5F2E8]/60">
              {s.winners_count} winner{s.winners_count === 1 ? "" : "s"}
            </span>
          </div>
          <div
            className="mt-4 inline-flex items-center gap-1 font-display uppercase text-sm tracking-widest transition-transform group-hover:translate-x-1"
            style={{ color: accent }}
          >
            View sponsor <ArrowRight size={14} />
          </div>
        </div>
      </Link>

      {sponsors.length > 1 && (
        <div className="mt-5 flex items-center gap-2 relative">
          {sponsors.map((sp, i) => (
            <button
              key={sp.id}
              onClick={() => setIdx(i)}
              aria-label={`Show sponsor ${i + 1}`}
              className="h-1 transition-all"
              style={{
                width: i === idx ? 32 : 12,
                background: i === idx ? accent : "rgba(245,242,232,0.2)",
              }}
              data-testid={`spotlight-dot-${i}`}
            />
          ))}
          <span className="ml-auto font-mono text-[10px] text-[#F5F2E8]/40 uppercase tracking-widest">
            {idx + 1} / {sponsors.length}
          </span>
        </div>
      )}
    </section>
  );
}
